/* eslint-disable react/prop-types */
import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { Link as HeadLink, Meta, Title } from 'react-head'
import { Link } from 'react-router-dom'
import sanitize from 'sanitize'
import DefaultLayout from '../../components/DefaultLayout'
import SubPageHeader from '../../components/SubPageHeader'
import ContentArea from '../../components/ContentArea'
import { DecodeHTML, GetCleanedPaths } from '../../utils'
import BlogFooter from './_BlogFooter'
import BlogMeta from './_BlogMeta'
import Author from '../../components/Author'
import ShareButtons from '../../components/ShareButtons'
import {
  getImageFromPost,
  getAuthorFromPost,
  getTermsFromPost,
} from '../../wpapi'
import Recipe from '../CMSTemplates/RecipeTemplate'
import { isSSR } from 'js/isSSR'
import withSSR, {
  propTypes as ssrPropTypes,
} from 'js/components/withSSR/withSSR'
import { useLocation } from 'react-router-dom'
import CMSContent from 'js/components/CMSContent/CMSContent'
import { useInView } from 'react-intersection-observer'
import loadable from '@loadable/component'
import Spinner from 'js/components/Spinner/Spinner'

const Comments = loadable(() => import('../../components/Comments'))
const Footer = loadable(() => import('../../components/Footer/Footer'))
const ErrorPage = loadable(() => import('../../pages/ErrorPage'))

function BlogPostPageContainer({ data, isLoading, error, match, refetch }) {
  useEffect(() => {
    refetch()
  }, [match.url])
  const location = useLocation()
  if (isLoading)
    return <SubPageHeader magenta={true} blog={true} loading={true} />

  if (error) return <ErrorPage></ErrorPage>
  const { id, content, acf, date, title } = data
  const author = getAuthorFromPost(data)
  const image = getImageFromPost(data)
  const { category, tags } = getTermsFromPost(data)

  if (!title && !content) return <ErrorPage></ErrorPage>

  return (
    <BlogPostPage
      id={id}
      content={content && content.rendered}
      acf={acf}
      title={title && title.rendered}
      image={image}
      author={author}
      category={category}
      tags={tags}
      date={date}
      location={location}
    ></BlogPostPage>
  )
}

BlogPostPageContainer.propTypes = ssrPropTypes

function MainImage({ src, alt = '' }) {
  if (!src) return null
  return (
    <div className="c-content-area__main-image-wrapper o-width-limiter o-width-limiter--mid">
      <img
        className="c-content-area__main-image"
        data-uid="blog-post-page-main-image"
        src={src}
        alt={alt}
      />
    </div>
  )
}

function Intro({ text, breakOut }) {
  if (!text) return null
  return (
    <div
      className={
        breakOut
          ? 'c-intro-text s-text  c-content-area__break-out'
          : 'c-intro-text s-text'
      }
      dangerouslySetInnerHTML={{
        __html: sanitize(text, {}),
      }}
    />
  )
}

function BlogPostPage({
  id,
  content,
  acf = {},
  title,
  date,
  author,
  image,
  category,
  tags,
  location,
}) {
  let text = content + acf.intro || ''
  let words = text.split(' ')
  const minutes = parseInt(words.length / 200, 10)
  const recipe = acf.post_is_recipe && acf.post_is_recipe[0] === '1'
  const readingTime = minutes < 1 ? 1 : minutes

  const [commentsRef, scrolledToComments] = useInView({ triggerOnce: true })

  useEffect(() => {
    if (!isSSR && document.querySelectorAll('.instagram-media').length) {
      let script = document.createElement('script')
      script.setAttribute('async', '')
      script.setAttribute('defer', '')
      script.setAttribute(
        'src',
        'https://platform.instagram.com/en_US/embeds.js'
      )
      document.querySelector('body').appendChild(script)
    }

    for (let video of document.querySelectorAll('video')) {
      video.setAttribute('autoplay', 'true')
    }

    // Remove empty "spacing" paragraphs
    for (let p of document.querySelectorAll('.s-text p')) {
      if (
        p.innerHTML === '&nbsp;' ||
        p.innerHTML === '<strong>&nbsp;</strong>'
      ) {
        p && p.parentNode.removeChild(p)
      }
    }

    // Replace old URL's (might be overkill?)
    document.querySelectorAll('a[href]').forEach(link => {
      let href = link.getAttribute('href')
      href = href.replace('lifesumcomprod.wpengine.com', 'lifesum.com/blog')
      href = href.replace('cms.lifesum.com', 'lifesum.com')
      href = href.replace(/\/\d+\/\d+\/\d+\//, '/') // Remove date slugs (e.g. /2017/01/01)
      href = href.replace('http://lifesum.com', 'https://lifesum.com')
      link.setAttribute('href', href)
    })
  }, [content])

  return (
    <DefaultLayout>
      <Title>{DecodeHTML(title)} - Lifesum</Title>
      {acf.description && (
        <Meta name="description" content={DecodeHTML(acf.description)} />
      )}
      {image && <Meta property="og:image" content={image.source_url}></Meta>}
      <HeadLink
        rel="canonical"
        href={
          'https://lifesum.com/' +
          GetCleanedPaths(true, location.pathname).join('/') +
          '/'
        }
      />
      <SubPageHeader
        heading={title}
        magenta={true}
        blog={true}
        smallHeading={true}
      />
      {image && (
        <MainImage src={image.source_url} alt={image.alt_text}></MainImage>
      )}
      <ContentArea>
        {author && !recipe && (
          <div className="o-space-between">
            <div className="c-meta">
              By{' '}
              <Link to={'/blog/author/' + author.slug}>
                <img
                  className="c-meta__avatar"
                  src={author.avatar_urls['48']}
                  alt=""
                />
                {author.name}
              </Link>
            </div>
            {readingTime && (
              <p className="c-reading-time">
                {readingTime} min
                <picture>
                  <source
                    srcSet={`${require('media/time.png')}, ${require('media/time@2x.png')} 2x`}
                  />
                  <img src={require('media/time.png')} alt="Reading time" />
                </picture>
              </p>
            )}
          </div>
        )}
        <Intro text={acf.intro} breakOut={recipe}></Intro>
        {recipe && <Recipe {...acf} instructions={content} />}
        {!recipe && content && (
          <div className="s-text">
            <CMSContent content={content}></CMSContent>
          </div>
        )}
        <ShareButtons title={title} />
        {recipe && (
          <div className="c-author">
            <img
              className="c-author__avatar"
              src={require('media/chef.jpg')}
              alt=""
            />
            <p className="c-author__bio leading-body">
              Find hundreds of healthy recipes in our app. All recipes comes
              with detailed nutritional information and are suggested to you
              based on your dietary preferences.
            </p>
            <Link className="c-author__link text-blue-dark" to="/signup">
              Sign up for Lifesum
            </Link>
          </div>
        )}
        {!recipe && <Author author={author} />}
        <BlogMeta date={date} category={category} tags={tags} />
        {content && (
          <div ref={commentsRef}>
            {scrolledToComments ? (
              <Comments id={id} date={date} />
            ) : (
              <div className="w-64 mx-auto">
                <Spinner />
              </div>
            )}
          </div>
        )}
      </ContentArea>
      <BlogFooter />
      {scrolledToComments && <Footer />}
    </DefaultLayout>
  )
}

BlogPostPage.propTypes = {
  post: PropTypes.shape({
    acf: PropTypes.object,
    date: PropTypes.object,
    id: PropTypes.string,
    content: PropTypes.shape({
      rendered: PropTypes.string,
    }),
    title: PropTypes.shape({
      rendered: PropTypes.string,
    }),
  }),
  location: PropTypes.object,
}

export default withSSR(BlogPostPageContainer)
